import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../utils/api';

interface InviteInfo {
  email: string;
  role: string;
  orgName: string;
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setLoadError('No invitation token provided');
      setLoading(false);
      return;
    }

    api.get<InviteInfo>(`/auth/invite/${token}`)
      .then(data => setInvite(data))
      .catch(err => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 10) {
      setError('Password must be at least 10 characters with uppercase, lowercase, number, and special character');
      return;
    }

    setSubmitting(true);
    try {
      const data: any = await api.post('/auth/accept-invite', {
        token,
        password,
        firstName,
        lastName,
      });

      // Auto-login: store token and user data
      api.setToken(data.token);
      localStorage.setItem('ppe_token', data.token);
      localStorage.setItem('ppe_user', JSON.stringify(data.user));
      window.location.href = '/';
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const roleName = invite?.role === 'quartermaster' ? 'Quartermaster' : invite?.role === 'admin' ? 'Admin' : 'Firefighter';

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
        <div style={{ color: '#6b7280', fontSize: 16 }}>Validating invitation...</div>
      </div>
    );
  }

  if (loadError || !invite) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', padding: 16 }}>
        <div style={{ width: '100%', maxWidth: 400 }}>
          <div style={{ background: 'white', padding: 32, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 12px', fontSize: 20, fontWeight: 600, color: '#dc2626' }}>Invalid Invitation</h2>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
              {loadError || 'This invitation link is invalid or has expired. Please ask your department admin for a new invitation.'}
            </p>
            <Link to="/login" style={{ color: '#b91c1c', fontWeight: 500 }}>Go to Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ background: '#b91c1c', color: 'white', display: 'inline-block', padding: '12px 24px', borderRadius: 12, fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            PPE Tracker
          </div>
        </div>

        <div style={{ background: 'white', padding: 32, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>Join {invite.orgName}</h2>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20, lineHeight: 1.4 }}>
            You've been invited to join as a <strong>{roleName}</strong>. Create your account below.
          </p>

          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 14px', borderRadius: 8, marginBottom: 20, fontSize: 13, color: '#166534' }}>
            Email: <strong>{invite.email}</strong>
          </div>

          {error && <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>First Name</label>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required autoFocus
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>Last Name</label>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>Password</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                placeholder="At least 10 characters" />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: '#374151' }}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                placeholder="Confirm your password" />
            </div>

            <button type="submit" disabled={submitting}
              style={{ width: '100%', padding: '12px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}>
              {submitting ? 'Creating Account...' : 'Create Account & Sign In'}
            </button>
          </form>

          <p style={{ marginTop: 20, textAlign: 'center', fontSize: 14, color: '#666' }}>
            Already have an account? <Link to="/login" style={{ color: '#b91c1c', fontWeight: 500 }}>Sign In</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
